import { yupResolver } from '@hookform/resolvers/yup';
import { ChevronDown, CurrencyDollar, Edit01, InfoCircle } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AdditionalServiceModal from 'src/app/MyQuote/Modals/AdditionalServiceModal';
import DangerousGoodsModal from 'src/app/MyQuote/Modals/DangerousGoodsModal';
import InBondModal from 'src/app/MyQuote/Modals/InBondModal';
import LimitedAccessDeliveryModal from 'src/app/MyQuote/Modals/LimitedAccessDeliveryModal';
import LoadInsuranceModal from 'src/app/MyQuote/Modals/LoadInsuranceModal';
import { DEFAULT_COMMODITIES } from 'src/app/MyQuote/Modals/quoteDetailModal.const';
import { handleCommodityChange } from 'src/app/MyQuote/Modals/quoteDetailModal.helper';
import { IDangerousGoodsForm, IInBondForm, ILimitedAccessDeliveryData, ILimitedAccessDeliveryDataForm, ILoadInsuranceForm } from 'src/app/MyQuote/Modals/quoteDetailModal.interface';
import CheckBox from 'src/components/CheckBox';
import InputText from 'src/components/InputText/InputText';
import InputTextArea from 'src/components/InputTextArea';
import SelectBox from 'src/components/SelectBox/SelectBox';
import Toggle from 'src/components/Toggle/Toggle';
import TooltipCmp from 'src/components/TooltipCmp';
import TruncateTextCmp from 'src/components/TruncateText';
import { CURRENCY, currencyOptions, MEASUREMENT } from 'src/constants/common';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { RootState } from 'src/redux/store';
import { getPricingRate } from 'src/services/CommonService';
import { logError } from 'src/services/LoggingService';
import { findServiceTypeIdBySlug, formatDecimalNumber } from 'src/utils/CommonFunctions';
import * as yup from 'yup';

import { QUOTE_KEYS } from '../createQuote.const';
import {
    completedSteps as completedStepsSelector,
    createQuoteData as createQuoteDataSelector,
    customerBillingAccountId as customerBillingAccountIdSelector,
    divisionId as divisionIdSelector,
    isCreateLoading as isCreateLoadingSelector,
    setIsCreateLoading,
} from '../CreateQuote.slice';

import StepperAction from './StepperAction';
import { useSaveNextStep } from './useSaveNextStep';

const initialAction = {
    pickupService: false,
    deliveryService: false,
    otherService: false,
    inBond: false,
    loadInsurance: false,
    dangerousGoods: false,
    limitedAccess: false,
    data: null as { [key: string]: any } | null,
};

interface IFtlCombinedForm {
    // Dimensions fields (FTL-relevant subset)
    totalWeight: string;
    linearFootage: string;
    weightMeasurement: string;
    serviceId: number[];
    additionalServices: {
        serviceId: number[];
        dangerousGoods: boolean;
        dangerGoodsType: string;
        description: string;
        emergencyContactExt: string;
        emergencyContactName: string;
        emergencyContactNumber: string;
        goodsClass: string;
        inbond: boolean;
        inbondType: string;
        inbondName: string;
        inbondAddress: string;
        inbondContactMethod: string;
        inbondContactValue: string;
        unitedNationsNumber: string;
        emergencyEapnbr: string;
        limitedAccess: boolean;
        limitedAccessType: string;
        limitedAccessOther: string;
        packagingGroup: string;
    };
    // Shipment fields
    primaryEquipmentType: string;
    primaryEquipmentTypeFullName: string;
    secondaryEquipmentTypes: string[];
    secondaryEquipmentTypeFullNames: string[];
    isLoadInsuranceEnabled: boolean;
    insuranceCommodity: string[];
    insuranceCommercialValue: string;
    insuranceCurrencyType: string;
    specialNotes: string;
}

const validationSchema = yup.object().shape({
    totalWeight: yup.string().test('total-weight-required', 'Total weight is required', function (value) {
        if (!value || value.trim().length === 0) return false;

        return Number(value) > 0;
    }),
    linearFootage: yup
        .string()
        .nullable()
        .test('required', 'Linear footage is required', function (value) {
            return Boolean(value && value.trim().length > 0);
        })
        .test('max', 'Linear footage must not exceed 53 ft', function (value) {
            if (!value || value.trim().length === 0) return true;
            const numericValue = parseFloat(value);

            if (isNaN(numericValue)) return true;

            return numericValue <= 53;
        }),
    primaryEquipmentType: yup.string().required('Primary equipment type is required'),
    primaryEquipmentTypeFullName: yup.string().required('Primary equipment type is required'),
    secondaryEquipmentTypes: yup.array().of(yup.string()).default([]),
    secondaryEquipmentTypeFullNames: yup.array().of(yup.string()).default([]),
    insuranceCommodity: yup.array().of(yup.string()).default([]),
    insuranceCommercialValue: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? null : value))
        .nullable()
        .test('value-required-when-enabled', 'Insurance commercial value is required', function (value) {
            const { isLoadInsuranceEnabled } = this.parent;

            if (isLoadInsuranceEnabled) {
                return value !== null && value !== undefined && typeof value === 'number' && !isNaN(value);
            }

            return true;
        })
        .test('positive-number', 'Insurance commercial value must be a positive number', function (value) {
            if (value === null || value === undefined || isNaN(value)) return true;

            return value > 0;
        })
        .typeError('Insurance commercial value must be a valid number'),
    insuranceCurrencyType: yup.string().default(CURRENCY.CAD),
});

interface FtlLoadShipmentDetailsProps {
    setHeaderChildren: (children: React.ReactNode) => void;
}

const FtlLoadShipmentDetails: React.FC<FtlLoadShipmentDetailsProps> = ({ setHeaderChildren }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { onSaveFtlCombined, onBackFtlCombined } = useSaveNextStep();

    const isCreateLoading = useAppSelector(isCreateLoadingSelector);
    const divisionId = useAppSelector(divisionIdSelector);
    const customerBillingAccountId = useAppSelector(customerBillingAccountIdSelector);
    const currentUser = useAppSelector(currentUserSelector);
    const createQuoteData = useAppSelector(createQuoteDataSelector);
    const completedSteps = useAppSelector(completedStepsSelector);

    const isAllPreviousStepsCompleted = useMemo(() => [QUOTE_KEYS.QUOTE_DETAILS, QUOTE_KEYS.SHIPPER_CONSIGNEE_DETAILS].every((step) => completedSteps.includes(step)), [completedSteps]);

    const { isVehicleTypeLoading, vehicleTypeOptions, services, serviceTypes, commodityOptions } = useSelector((state: RootState) => state.basic);

    const [action, setAction] = useState(initialAction);
    const [commodityInputValue, setCommodityInputValue] = useState<string>('');
    const [filteredCommodityOptions, setFilteredCommodityOptions] = useState<any>([]);
    const [isCommodityMenuOpen, setIsCommodityMenuOpen] = useState<boolean>(false);
    const [commoditySelectKey, setCommoditySelectKey] = useState<number>(0);
    const [currencyRates, setCurrencyRates] = useState<{ cadRate: number; usdRate: number }>({ cadRate: 1, usdRate: 0.75 });
    const [isEquipmentExpanded, setIsEquipmentExpanded] = useState(false);
    const prevInsuranceCurrencyRef = useRef<string | null>(null);
    const isConvertingCurrencyRef = useRef<boolean>(false);
    const serviceRefName = useRef<HTMLParagraphElement>(null);
    const hasInitializedServices = useRef<boolean>(false);

    const defaultCurrency = useMemo(() => {
        const selectedDivision: any = currentUser?.divisions?.find((d: any) => d?.id === divisionId);
        const selectedBillingAccount = selectedDivision?.customerBillingAccount?.find((b: any) => b?.id === customerBillingAccountId);

        return selectedBillingAccount?.currency?.toUpperCase();
    }, [currentUser, divisionId, customerBillingAccountId]);

    const parseToArray = (value: any): string[] => {
        if (!value) return [];
        if (Array.isArray(value)) return value.filter((item: string) => item?.trim() !== '');
        if (typeof value === 'string') return value.split('|').filter((item: string) => item.trim() !== '');

        return [];
    };

    const defaultValues: IFtlCombinedForm = useMemo(() => {
        const dim = createQuoteData?.dimensionsDetails;
        const ship = createQuoteData?.shipmentDetails;

        return {
            // Dimensions defaults
            totalWeight: dim?.totalWeight ? String(dim.totalWeight) : '',
            linearFootage: dim?.linearFootage ? String(dim.linearFootage) : '53',
            weightMeasurement: dim?.weightMeasurement ? dim.weightMeasurement.toUpperCase() : MEASUREMENT.WEIGHT1.value,
            serviceId: dim?.serviceId || [],
            additionalServices: {
                serviceId: dim?.additionalServices?.serviceId || [],
                dangerousGoods: dim?.additionalServices?.dangerousGoods || false,
                dangerGoodsType: dim?.additionalServices?.dangerGoodsType || '',
                description: dim?.additionalServices?.description || '',
                emergencyContactExt: dim?.additionalServices?.emergencyContactExt || '',
                emergencyContactName: dim?.additionalServices?.emergencyContactName || '',
                emergencyContactNumber: dim?.additionalServices?.emergencyContactNumber || '',
                goodsClass: dim?.additionalServices?.goodsClass || '',
                inbond: dim?.additionalServices?.inbond || false,
                inbondType: dim?.additionalServices?.inbondType || '',
                inbondName: dim?.additionalServices?.inbondName || '',
                inbondAddress: dim?.additionalServices?.inbondAddress || '',
                inbondContactMethod: dim?.additionalServices?.inbondContactMethod || '',
                inbondContactValue: dim?.additionalServices?.inbondContactValue || '',
                unitedNationsNumber: dim?.additionalServices?.unitedNationsNumber || '',
                emergencyEapnbr: dim?.additionalServices?.emergencyEapnbr || '',
                limitedAccess: dim?.additionalServices?.limitedAccess || false,
                limitedAccessType: dim?.additionalServices?.limitedAccessType || '',
                limitedAccessOther: dim?.additionalServices?.limitedAccessOther || '',
                packagingGroup: dim?.additionalServices?.packagingGroup || '',
            },
            // Shipment defaults
            primaryEquipmentType: ship?.primaryEquipmentType || 'V',
            primaryEquipmentTypeFullName: ship?.primaryEquipmentTypeFullName || 'Dry Van',
            secondaryEquipmentTypeFullNames: parseToArray(ship?.secondaryEquipmentTypeFullNames),
            secondaryEquipmentTypes: parseToArray(ship?.secondaryEquipmentTypes),
            isLoadInsuranceEnabled: ship?.isLoadInsuranceEnabled ?? false,
            insuranceCommodity: parseToArray(ship?.insuranceCommodity),
            insuranceCommercialValue: ship?.insuranceCommercialValue || '',
            insuranceCurrencyType: ship?.insuranceCurrencyType || (defaultCurrency ?? CURRENCY.CAD),
            specialNotes: ship?.specialNotes || '',
        };
    }, [createQuoteData?.dimensionsDetails, createQuoteData?.shipmentDetails, defaultCurrency]);

    const {
        control,
        watch,
        setValue,
        handleSubmit,
        getValues,
        trigger,
        clearErrors,
        formState: { isSubmitted },
    } = useForm<IFtlCombinedForm>({
        resolver: yupResolver(validationSchema) as any,
        defaultValues,
    });

    const watchServiceId = watch('serviceId');
    const primaryEquipmentType = watch('primaryEquipmentType');
    const secondaryEquipmentTypes = watch('secondaryEquipmentTypes');
    const isLoadInsuranceEnabled = watch('isLoadInsuranceEnabled');
    const insuranceCommercialValue = watch('insuranceCommercialValue');
    const insuranceCurrencyType = watch('insuranceCurrencyType');

    const convertCurrency = useCallback(
        (value: number, fromCurrency: string, toCurrency: string): number => {
            if (!value || value === 0 || fromCurrency === toCurrency) return formatDecimalNumber(value || 0, 2);

            let convertedValue = 0;

            if (fromCurrency === CURRENCY.CAD && toCurrency === CURRENCY.USD) {
                convertedValue = value * (currencyRates.usdRate || 0.75);
            } else if (fromCurrency === CURRENCY.USD && toCurrency === CURRENCY.CAD) {
                convertedValue = value * (currencyRates.cadRate || 1);
            } else {
                convertedValue = value;
            }

            return formatDecimalNumber(convertedValue, 2);
        },
        [currencyRates]
    );

    // Pre-populate pickup/delivery services from shipper/consignee address book on first visit
    useEffect(() => {
        if (hasInitializedServices.current) return;
        hasInitializedServices.current = true;

        if (!createQuoteData?.dimensionsDetails?.serviceId?.length) {
            const shipperServiceIds: number[] = (createQuoteData?.shipperConsigneeDetails?.shipperAddress?.additionalServiceIds || []).map(Number).filter(Boolean);
            const consigneeServiceIds: number[] = (createQuoteData?.shipperConsigneeDetails?.consigneeAddress?.additionalServiceIds || []).map(Number).filter(Boolean);
            const combined = [...shipperServiceIds, ...consigneeServiceIds];
            const combinedIds = combined.filter((id, index) => combined.indexOf(id) === index);

            if (combinedIds.length > 0) {
                setValue('serviceId', combinedIds);
            }
        }
    }, []);

    // Fetch currency rates for insurance value conversion
    useEffect(() => {
        getPricingRate()
            .then((response: any) => {
                if (response?.data) {
                    setCurrencyRates({
                        cadRate: response.data.cadRate || 1,
                        usdRate: response.data.usdRate || 0.75,
                    });
                }
            })
            .catch(async (err: any) => {
                await logError(err, {
                    errorMessage: err?.message || 'Error while fetching pricing rates',
                    errorLocation: 'FtlLoadShipmentDetails: getPricingRate',
                    storeInDatabase: true,
                    logToConsole: true,
                });
            });
    }, []);

    // Initialize commodity options
    useEffect(() => {
        if (commodityOptions && commodityOptions.length > 0) {
            setFilteredCommodityOptions(commodityOptions);
        }
    }, [commodityOptions]);

    // Initialize currency ref on first render
    useEffect(() => {
        if (prevInsuranceCurrencyRef.current === null && insuranceCurrencyType) {
            prevInsuranceCurrencyRef.current = insuranceCurrencyType;
        }
    }, [insuranceCurrencyType]);

    // Auto-convert commercial value when currency changes
    useEffect(() => {
        if (prevInsuranceCurrencyRef.current && prevInsuranceCurrencyRef.current !== insuranceCurrencyType && insuranceCommercialValue && !isConvertingCurrencyRef.current) {
            isConvertingCurrencyRef.current = true;
            const numericValue = parseFloat(String(insuranceCommercialValue)) || 0;

            if (numericValue > 0) {
                const convertedValue = convertCurrency(numericValue, prevInsuranceCurrencyRef.current, insuranceCurrencyType);
                setValue('insuranceCommercialValue', String(convertedValue));

                if (isSubmitted) trigger('insuranceCommercialValue');
            }

            setTimeout(() => {
                isConvertingCurrencyRef.current = false;
            }, 100);
        }
        prevInsuranceCurrencyRef.current = insuranceCurrencyType;
    }, [insuranceCurrencyType, insuranceCommercialValue]);

    // Set default commodity when primary equipment or insurance toggle changes
    const setDefaultCommodity = useCallback(() => {
        if (primaryEquipmentType) {
            const currentCommodity = getValues('insuranceCommodity') || [];
            const newCommodity = handleCommodityChange(primaryEquipmentType, currentCommodity);

            if (newCommodity && Array.isArray(newCommodity) && newCommodity.length > 0) {
                setValue('insuranceCommodity', newCommodity);
            } else if (Array.isArray(currentCommodity) && currentCommodity.length === 1 && DEFAULT_COMMODITIES.includes(currentCommodity[0])) {
                setValue('insuranceCommodity', []);
            }
        }
    }, [primaryEquipmentType, isLoadInsuranceEnabled, getValues, setValue]);

    useEffect(() => {
        setDefaultCommodity();
    }, [primaryEquipmentType]);

    const handleClose = useCallback(() => {
        setAction(initialAction);
    }, []);

    // ─── Service handlers (same pattern as AddDimensions) ────────────────────────

    const EDITABLE_SERVICES_SLUGS = ['in-bond', 'load_insurance', 'dangerous_goods', 'limited_access_delivery'];

    const textReplacements = [
        { original: 'After Hours / Weekend / Holiday Pickup', replacement: t('QuotingHub.Common.After_Hours_Pickup') },
        { original: 'Pickup Appointment Required', replacement: t('QuotingHub.Common.Pickup_Appointment') },
        { original: 'Delivery Appointment Required', replacement: t('QuotingHub.Common.Delivery_Appointment') },
    ];

    const getReplacedText = (text: string) => {
        const replacementItem = textReplacements.find((item) => item.original === text);

        return replacementItem ? replacementItem.replacement : text;
    };

    const handleServiceChange = (e: any) => {
        let { checked, name, value } = e.target;
        value = parseInt(value);

        if (checked) {
            if (name === 'in-bond') {
                setAction({ ...action, inBond: true });
            } else if (name === 'load_insurance') {
                setAction({ ...action, loadInsurance: true });
            } else if (name === 'dangerous_goods') {
                setAction({ ...action, dangerousGoods: true });
            } else if (name === 'limited_access_delivery') {
                setAction({ ...action, limitedAccess: true });
            } else {
                setValue('serviceId', [...watchServiceId, value]);
            }
        } else {
            const filteredServices = watchServiceId.filter((item: any) => item !== value);
            setValue('serviceId', filteredServices);

            if (name === 'in-bond') {
                setValue('additionalServices.inbond', false);
                setValue('additionalServices.inbondType', '');
                setValue('additionalServices.inbondName', '');
                setValue('additionalServices.inbondAddress', '');
                setValue('additionalServices.inbondContactMethod', '');
                setValue('additionalServices.inbondContactValue', '');
            } else if (name === 'load_insurance') {
                setValue('additionalServices.insuranceCommercialValue' as any, '');
                setValue('additionalServices.insuranceCurrencyType' as any, CURRENCY.CAD);
                setValue('additionalServices.insuranceCommodity' as any, '');
            } else if (name === 'dangerous_goods') {
                setValue('additionalServices.dangerousGoods', false);
                setValue('additionalServices.dangerGoodsType', '');
                setValue('additionalServices.unitedNationsNumber', '');
                setValue('additionalServices.packagingGroup', '');
                setValue('additionalServices.goodsClass', '');
                setValue('additionalServices.description', '');
                setValue('additionalServices.emergencyContactName', '');
                setValue('additionalServices.emergencyContactNumber', '');
                setValue('additionalServices.emergencyContactExt', '');
                setValue('additionalServices.emergencyEapnbr', '');
            } else if (name === 'limited_access_delivery') {
                setValue('additionalServices.limitedAccess', false);
                setValue('additionalServices.limitedAccessType', '');
                setValue('additionalServices.limitedAccessOther', '');
            }
        }
    };

    const handleEditService = (service: any) => () => {
        if (service?.slug === 'in-bond') {
            setAction({ ...action, inBond: true });
        } else if (service?.slug === 'load_insurance') {
            setAction({ ...action, loadInsurance: true });
        } else if (service?.slug === 'dangerous_goods') {
            setAction({ ...action, dangerousGoods: true });
        } else if (service?.slug === 'limited_access_delivery') {
            setAction({ ...action, limitedAccess: true });
        }
    };

    const getList = useCallback(
        (data: any, excludedServices: string[] = []) => (
            <>
                {data.map((val: any) =>
                    !excludedServices.includes(val.slug) && (val?.type !== 'OTHER' || val?.slug === 'load_insurance' || val.loadLinkTerm !== null) ? (
                        <div className="flex w-[calc(33.33%_-_8px)]" key={`service_${val.id}`}>
                            <CheckBox
                                id={`service_${val.id}`}
                                onChangeFunc={handleServiceChange}
                                name={val.slug}
                                value={val.id}
                                checked={watchServiceId.includes(val.id) || false}
                                parentClassName="mt-px"
                            />
                            <div className="pl-2 leading-4 text-textSecondary text-xs font-medium truncate flex-1">
                                {val.slug === 'pickup_appointment_required' || val.slug === 'delivery_appointment_required' || val.slug === 'after_hours_pickup' ? (
                                    <TooltipCmp message={val.name} childrenClassName="!flex">
                                        <label htmlFor={`service_${val.id}`} className={`cursor-pointer truncate`}>
                                            {getReplacedText(val.name)}
                                        </label>
                                    </TooltipCmp>
                                ) : (
                                    <TruncateTextCmp message={val.name} refName={serviceRefName}>
                                        <p className="truncate" ref={serviceRefName}>
                                            <label htmlFor={`service_${val.id}`} className="cursor-pointer">
                                                {getReplacedText(val.name)}
                                            </label>
                                        </p>
                                    </TruncateTextCmp>
                                )}
                            </div>
                            {watchServiceId.includes(val.id) && EDITABLE_SERVICES_SLUGS.includes(val.slug) ? (
                                <TooltipCmp message={t('Common.Edit')}>
                                    <Edit01 className="ml-2 mt-px w-3.5 h-3.5 cursor-pointer flex-none hover:text-primary" onClick={handleEditService(val)} />
                                </TooltipCmp>
                            ) : null}
                        </div>
                    ) : (
                        ''
                    )
                )}
            </>
        ),
        [watchServiceId]
    );

    const handleLimitedAccess = (data: ILimitedAccessDeliveryData) => {
        setValue('additionalServices.limitedAccess', true);
        setValue('additionalServices.limitedAccessOther', data.limitedAccessOther);
        setValue('additionalServices.limitedAccessType', data.selectedLimitedAccessType);
        const limitedAccessId = serviceTypes.DELIVERY?.find((val: any) => val.slug === 'limited_access_delivery')?.id;

        if (!watchServiceId.includes(Number(limitedAccessId))) {
            setValue('serviceId', [...watchServiceId, Number(limitedAccessId)]);
        }
    };

    const handleDangerousGoods = (data: IDangerousGoodsForm) => {
        const loadInsuranceId = findServiceTypeIdBySlug(serviceTypes.OTHER, 'dangerous_goods');

        if (!watchServiceId.includes(Number(loadInsuranceId))) {
            setValue('serviceId', [...watchServiceId, Number(loadInsuranceId)]);
        }
        setValue('additionalServices.dangerousGoods', true);
        setValue('additionalServices.dangerGoodsType', data.dangerGoodsType);
        setValue('additionalServices.unitedNationsNumber', data.unitedNationsNumber);
        setValue('additionalServices.packagingGroup', data.packagingGroup);
        setValue('additionalServices.goodsClass', data.goodsClass);
        setValue('additionalServices.description', data.description);
        setValue('additionalServices.emergencyContactName', data.emergencyContactName);
        setValue('additionalServices.emergencyContactNumber', data.emergencyContactNumber);
        setValue('additionalServices.emergencyContactExt', data.emergencyContactExt);
        setValue('additionalServices.emergencyEapnbr', data.emergencyEapnbr);
        handleClose();
    };

    const handleInBond = (data: any) => {
        const inBondId = findServiceTypeIdBySlug(serviceTypes.OTHER, 'in-bond');

        if (!watchServiceId.includes(Number(inBondId))) {
            setValue('serviceId', [...watchServiceId, Number(inBondId)]);
        }
        setValue('additionalServices.inbond', true);
        setValue('additionalServices.inbondType', data.inbondType);
        setValue('additionalServices.inbondName', data.inbondName);
        setValue('additionalServices.inbondAddress', data.inbondAddress);
        setValue('additionalServices.inbondContactMethod', data.inbondContactMethod);
        setValue('additionalServices.inbondContactValue', data.inbondContactValue);
        handleClose();
    };

    const handleLoadInsurance = (data: ILoadInsuranceForm) => {
        const loadInsuranceId = findServiceTypeIdBySlug(serviceTypes.OTHER, 'load_insurance');
        setValue('serviceId', [...watchServiceId, Number(loadInsuranceId)]);
        setValue('additionalServices.insuranceCommercialValue' as any, data.insuranceCommercialValue);
        setValue('additionalServices.insuranceCurrencyType' as any, data.insuranceCurrencyType);
        setValue('additionalServices.insuranceCommodity' as any, data.insuranceCommodity);
        handleClose();
    };

    // ─── Equipment type handlers (same pattern as ShipmentDetails) ───────────────

    const handlePrimaryEquipmentChange = (onChange: any) => (event: any) => {
        onChange(event?.label);
        setValue('primaryEquipmentType', event?.value);

        // Remove primary from secondary if it exists
        const currentSecondaryTypes = secondaryEquipmentTypes || [];
        const filteredTypes = currentSecondaryTypes.filter((type: string) => type !== event?.value);
        setValue('secondaryEquipmentTypes', filteredTypes);

        const currentSecondaryNames = watch('secondaryEquipmentTypeFullNames') || [];
        const filteredNames = currentSecondaryNames.filter((name: string) => name !== event?.label);
        setValue('secondaryEquipmentTypeFullNames', filteredNames);
    };

    const handleSecondaryEquipmentChange = (vehicleType: any) => (e: any) => {
        const currentSecondaryTypes = secondaryEquipmentTypes || [];
        const currentSecondaryNames = watch('secondaryEquipmentTypeFullNames') || [];

        if (e.target.checked) {
            setValue('secondaryEquipmentTypes', [...currentSecondaryTypes, e.target.value]);
            setValue('secondaryEquipmentTypeFullNames', [...currentSecondaryNames, vehicleType.label]);
        } else {
            setValue(
                'secondaryEquipmentTypes',
                currentSecondaryTypes.filter((item: string) => item !== e.target.value)
            );
            setValue(
                'secondaryEquipmentTypeFullNames',
                currentSecondaryNames.filter((item: string) => item !== vehicleType.label)
            );
        }
    };

    // ─── Load Insurance handlers (same pattern as ShipmentDetails) ───────────────

    const handleLoadInsuranceToggle = (onChange: any) => (e: any) => {
        onChange(e.target.checked);

        if (!e.target.checked) {
            setValue('insuranceCommercialValue', '');
            clearErrors(['insuranceCommercialValue']);
        }

        if (isSubmitted) {
            trigger('insuranceCommercialValue');
            trigger('insuranceCommodity');
        }
    };

    const handleInsuranceCommodityMultipleChange = (onChange: any) => (selectedOptions: any) => {
        if (selectedOptions && Array.isArray(selectedOptions)) {
            onChange(selectedOptions.map((option: any) => option.value));
        } else {
            onChange([]);
        }

        setCommodityInputValue('');
        setFilteredCommodityOptions(commodityOptions);

        if (isSubmitted) trigger('insuranceCommercialValue');
    };

    const handleCreateCommodityOption = (onChange: any) => (inputValue: string) => {
        const trimmedValue = inputValue?.trim();

        if (trimmedValue) {
            const currentValue = watch('insuranceCommodity') || [];
            const newValue = Array.isArray(currentValue) ? [...currentValue, trimmedValue] : [trimmedValue];
            onChange(newValue);
            setCommodityInputValue('');
            setFilteredCommodityOptions(commodityOptions);
            setCommoditySelectKey((prev) => prev + 1);
            if (isSubmitted) trigger('insuranceCommodity');
        }
    };

    const handleCommodityInputChange = (inputValue: string, actionMeta: any) => {
        if (actionMeta.action === 'input-change') {
            setCommodityInputValue(inputValue);

            if (!inputValue.trim()) {
                setFilteredCommodityOptions(commodityOptions);
            } else {
                const filtered = commodityOptions.filter((option: any) => {
                    const label = typeof option === 'string' ? option : option.label || '';
                    const value = typeof option === 'string' ? option : option.value || '';

                    return (
                        (label && typeof label === 'string' && label.toLowerCase().includes(inputValue.toLowerCase())) ||
                        (value && typeof value === 'string' && value.toLowerCase().includes(inputValue.toLowerCase()))
                    );
                });
                setFilteredCommodityOptions(filtered);
            }
        } else if (actionMeta.action === 'menu-close') {
            setCommodityInputValue('');
        }
    };

    const handleCommodityMenuOpen = () => {
        setIsCommodityMenuOpen(true);
        setFilteredCommodityOptions(commodityOptions);
        setCommodityInputValue('');
    };

    const handleCommodityMenuClose = () => {
        setIsCommodityMenuOpen(false);
        setCommodityInputValue('');
        setFilteredCommodityOptions(commodityOptions);
    };

    const handleClearCommodities = (onChange: any) => () => {
        setIsCommodityMenuOpen(false);
        onChange([]);
        setCommodityInputValue('');
        setFilteredCommodityOptions(commodityOptions);
    };

    const handleCommodityKeyDown = useCallback(
        (onChange: any, currentValue: string[]) => (event: React.KeyboardEvent) => {
            if (event.key === 'Backspace' && !commodityInputValue && Array.isArray(currentValue) && currentValue.length > 0) {
                event.preventDefault();
                const newValue = currentValue.slice(0, -1);
                onChange(newValue);
                if (isSubmitted) trigger('insuranceCommodity');
            }
        },
        [commodityInputValue, isSubmitted, trigger]
    );

    const handleInsuranceCommercialValueChange = (onChange: any) => (e: any) => {
        onChange(e.target.value);
        if (isSubmitted) trigger('insuranceCommodity');
    };

    const handleInsuranceCurrencyChange = (onChange: any) => (selectedOption: any) => {
        onChange(selectedOption?.value || CURRENCY.USD);
    };

    // ─── Submit / Back ────────────────────────────────────────────────────────────

    const onSubmit = useCallback(
        (data: IFtlCombinedForm) => {
            dispatch(setIsCreateLoading(true));

            const dimensionsPayload = {
                weightMeasurement: data.weightMeasurement || 'LBS',
                lengthMeasurement: 'IN',
                totalWeight: data.totalWeight || null,
                linearFootage: data.linearFootage || null,
                dimensions: [],
                serviceId: data.serviceId || [],
                servicesData: data.serviceId?.map((item: any) => services.find((val: any) => val.id === item)) || [],
                additionalServices: {
                    serviceId: data.serviceId || [],
                    dangerousGoods: data.additionalServices?.dangerousGoods || false,
                    dangerGoodsType: data.additionalServices?.dangerGoodsType || '',
                    description: data.additionalServices?.description || '',
                    emergencyContactExt: data.additionalServices?.emergencyContactExt || '',
                    emergencyContactName: data.additionalServices?.emergencyContactName || '',
                    emergencyContactNumber: data.additionalServices?.emergencyContactNumber || '',
                    goodsClass: data.additionalServices?.goodsClass || '',
                    inbond: data.additionalServices?.inbond || false,
                    inbondType: data.additionalServices?.inbondType || '',
                    inbondName: data.additionalServices?.inbondName || '',
                    inbondAddress: data.additionalServices?.inbondAddress || '',
                    inbondContactMethod: data.additionalServices?.inbondContactMethod || '',
                    inbondContactValue: data.additionalServices?.inbondContactValue || '',
                    unitedNationsNumber: data.additionalServices?.unitedNationsNumber || '',
                    emergencyEapnbr: data.additionalServices?.emergencyEapnbr || '',
                    limitedAccess: data.additionalServices?.limitedAccess || false,
                    limitedAccessType: data.additionalServices?.limitedAccessType || '',
                    limitedAccessOther: data.additionalServices?.limitedAccessOther || '',
                    packagingGroup: data.additionalServices?.packagingGroup || '',
                },
            };

            const shipmentPayload = {
                primaryEquipmentType: data.primaryEquipmentType,
                primaryEquipmentTypeFullName: data.primaryEquipmentTypeFullName,
                secondaryEquipmentTypes: data.secondaryEquipmentTypes?.join(','),
                secondaryEquipmentTypeFullNames: data.secondaryEquipmentTypeFullNames?.join(','),
                isLoadInsuranceEnabled: data.isLoadInsuranceEnabled,
                insuranceCommodity: data.insuranceCommodity?.length > 0 ? data.insuranceCommodity.join('|') : null,
                insuranceCommercialValue: data.insuranceCommercialValue,
                insuranceCurrencyType: data.insuranceCurrencyType,
                specialNotes: data.specialNotes || '',
            };

            onSaveFtlCombined(dimensionsPayload, shipmentPayload);
        },
        [dispatch, onSaveFtlCombined, services]
    );

    const handleNext = useCallback(async () => {
        const isValid = await trigger();

        if (!isValid) return false;
        handleSubmit(onSubmit)();
    }, [trigger, handleSubmit, onSubmit]);

    const handleBack = useCallback(() => {
        const data = getValues();

        const dimensionsPayload = {
            weightMeasurement: data.weightMeasurement || 'LBS',
            lengthMeasurement: 'IN',
            totalWeight: data.totalWeight || null,
            linearFootage: data.linearFootage || null,
            dimensions: [],
            serviceId: data.serviceId || [],
            additionalServices: data.additionalServices,
        };

        const shipmentPayload = {
            primaryEquipmentType: data.primaryEquipmentType,
            primaryEquipmentTypeFullName: data.primaryEquipmentTypeFullName,
            secondaryEquipmentTypes: Array.isArray(data.secondaryEquipmentTypes) ? data.secondaryEquipmentTypes.join(',') : data.secondaryEquipmentTypes || '',
            secondaryEquipmentTypeFullNames: Array.isArray(data.secondaryEquipmentTypeFullNames) ? data.secondaryEquipmentTypeFullNames.join(',') : data.secondaryEquipmentTypeFullNames || '',
            isLoadInsuranceEnabled: data.isLoadInsuranceEnabled,
            insuranceCommodity: Array.isArray(data.insuranceCommodity) && data.insuranceCommodity.length > 0 ? data.insuranceCommodity.join('|') : null,
            insuranceCommercialValue: data.insuranceCommercialValue,
            insuranceCurrencyType: data.insuranceCurrencyType,
        };

        onBackFtlCombined(dimensionsPayload, shipmentPayload);
    }, [getValues, onBackFtlCombined]);

    useEffect(() => {
        setHeaderChildren?.(
            <StepperAction
                handleNext={handleNext}
                handleBack={handleBack}
                isNextButtonLoading={isCreateLoading}
                nextButtonText={isAllPreviousStepsCompleted ? t('QuotingHub.Create_Quote') : t('Common.Next')}
            />
        );

        return () => {
            setHeaderChildren?.(null);
        };
    }, [isCreateLoading, isAllPreviousStepsCompleted, handleNext, handleBack, t]);

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <>
            {/* ── Section 1: Load Overview ─────────────────────────────────────── */}
            <div className="stepper-content-container">
                <div className="stepper-lable-wrap">
                    <label className="stepper-label-common">
                        {t('Common.Load_Overview')}
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                <div className="flex items-start gap-x-3 flex-1">
                    <Controller
                        control={control}
                        name="totalWeight"
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <InputText
                                inputName="totalWeight"
                                placeholder={t('QuotingHub.Common.Enter_total_weight')}
                                parentClassName="flex-1"
                                value={value || ''}
                                onChangeFunc={onChange}
                                inputType="number"
                                isAllowDecimal={true}
                                shouldFormatNumber={true}
                                maxLength={7}
                                errorText={error?.message && error?.type === 'max' ? error?.message : !!error}
                                isError={!!error?.message}
                                inputValueUnitType={watch('weightMeasurement')?.toLowerCase()}
                                autoFocus={true}
                            />
                        )}
                    />
                    <Controller
                        control={control}
                        name="linearFootage"
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <InputText
                                inputName="linearFootage"
                                placeholder={t('QuotingHub.Common.Enter_linear_footage')}
                                parentClassName="flex-1"
                                value={value || ''}
                                onChangeFunc={onChange}
                                shouldFormatNumber={true}
                                maxLength={2}
                                inputValueUnitType="ft"
                                errorText={error?.message && error?.type === 'max' ? error?.message : ''}
                                isIconError={!!error?.message}
                                inputType="number"
                                isAllowDecimal={false}
                                isError={!!error}
                            />
                        )}
                    />
                </div>
            </div>

            {/* ── Section 2: Commodity ─────────────────────────────────────────── */}
            <div className="border-t border-utilityGray200 stepper-content-container w-full max-w-[825px]">
                <div className="stepper-lable-wrap">
                    <label className="stepper-label-common">{t('Common.Commodity_Type')}</label>
                </div>
                <div className="stepper-lable-content-wrap flex-1 max-w-[590px]">
                    <Controller
                        name="insuranceCommodity"
                        control={control}
                        render={({ field: { onChange, value }, fieldState: { error } }) => {
                            const selectedOptions =
                                (Array.isArray(value) ? value : [])
                                    .map((val: string) => {
                                        const findValue = commodityOptions.find((option: any) => option.value === val);

                                        if (findValue) return findValue;

                                        if (val && val.trim()) return { value: val, label: val };

                                        return null;
                                    })
                                    .filter(Boolean) || [];

                            const optionsToDisplay = filteredCommodityOptions.length > 0 || commodityInputValue.trim() ? filteredCommodityOptions : commodityOptions;

                            return (
                                <SelectBox
                                    key={`commodity-select-${commoditySelectKey}`}
                                    SelectDropdownWrapClass="multi-select-v2-dropdown"
                                    classComp="!bg-white"
                                    options={optionsToDisplay}
                                    value={selectedOptions}
                                    onChangeFunc={handleInsuranceCommodityMultipleChange(onChange)}
                                    onInputChange={handleCommodityInputChange}
                                    placeholder={t('Common.Select_commodity')}
                                    errorText={error?.message ? true : false}
                                    isClearable={true}
                                    isMultipleSelection={true}
                                    closeMenuOnSelect={false}
                                    onClearFunc={handleClearCommodities(onChange)}
                                    isMultipleEquipmentType={true}
                                    maxVisibleValues={8}
                                    keepMenuOpenOnMultiSelect={true}
                                    autoCompleteSelect={true}
                                    onCreateOption={handleCreateCommodityOption(onChange)}
                                    isValidNewOption={Boolean(commodityInputValue && filteredCommodityOptions.length === 0)}
                                    createOptionText="Add new commodity"
                                    isSearchable={true}
                                    forceSearchable={true}
                                    noOptionMessage="No options found"
                                    onMenuOpen={handleCommodityMenuOpen}
                                    onMenuClose={handleCommodityMenuClose}
                                    menuIsOpen={isCommodityMenuOpen}
                                    maintainFocusOnClear={true}
                                    isShowInputValue={true}
                                    defaultValueInput={commodityInputValue}
                                    isHideCheckbox
                                    onKeyDown={handleCommodityKeyDown(onChange, value || [])}
                                />
                            );
                        }}
                    />
                </div>
            </div>

            {/* ── Section 3: Primary Equipment Type ───────────────────────────── */}
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">
                        Primary Equipment Type
                        <span className="text-red-600 ml-[2px] leading-4 text-xs">*</span>
                    </label>
                </div>
                <div className="flex-1 space-y-3">
                    <Controller
                        name="primaryEquipmentTypeFullName"
                        control={control}
                        render={({ field: { value, onChange }, fieldState: { error } }) => (
                            <SelectBox
                                required
                                name="primaryEquipmentType"
                                placeholder="Select equipment type"
                                label=""
                                labelClassName="form_label mb-1.5 block"
                                options={vehicleTypeOptions}
                                value={vehicleTypeOptions.find((item: any) => value === item.label)}
                                isLoading={isVehicleTypeLoading}
                                isMultipleSelection={false}
                                onChangeFunc={handlePrimaryEquipmentChange(onChange)}
                                errorText={error?.message ? true : false}
                            />
                        )}
                    />
                </div>
            </div>

            {/* ── Section 4: Secondary Equipment Type ─────────────────────────── */}
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex items-center justify-between cursor-pointer">
                    <label className="stepper-label-common cursor-pointer">Secondary Equipment Type</label>
                </div>
                <div className="flex-1 rounded-lg border border-utilityGray200 bg-white p-1.5 flex flex-wrap relative">
                    <div className="absolute right-4 top-3 ">
                        <TooltipCmp message={isEquipmentExpanded ? t('Common.Collapse') : t('Common.Expand')} childrenClassName="!flex cursor-pointer">
                            <div onClick={() => setIsEquipmentExpanded((prev) => !prev)}>
                                <ChevronDown className={`w-4 h-4 text-grayLight600 transition-transform duration-300 ${isEquipmentExpanded ? 'rotate-180' : 'rotate-0'}`} />
                            </div>
                        </TooltipCmp>
                    </div>
                    {vehicleTypeOptions
                        .filter((vehicleType: any) => vehicleType.value !== primaryEquipmentType)
                        .slice(0, 3)
                        .map((vehicleType: any, index: number) => (
                            <div className="w-1/3 p-1.5 flex items-center" key={`equipment_type_${index}`}>
                                <CheckBox
                                    id={`secondary_${vehicleType.value}`}
                                    name={vehicleType.label}
                                    checked={primaryEquipmentType === vehicleType.value || secondaryEquipmentTypes?.includes(vehicleType.value)}
                                    parentClassName="!w-3.5 !h-3.5"
                                    value={vehicleType.value}
                                    onChangeFunc={handleSecondaryEquipmentChange(vehicleType)}
                                    disabled={false}
                                />
                                <label htmlFor={`secondary_${vehicleType.value}`} className="text-textSecondary text-xs font-medium pl-1.5 cursor-pointer">
                                    {vehicleType.label}
                                </label>
                            </div>
                        ))}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateRows: isEquipmentExpanded ? '1fr' : '0fr',
                            transition: 'grid-template-rows 300ms ease',
                            width: '100%',
                        }}
                    >
                        <div className="overflow-hidden flex flex-wrap">
                            {vehicleTypeOptions
                                .filter((vehicleType: any) => vehicleType.value !== primaryEquipmentType)
                                .slice(3)
                                .map((vehicleType: any, index: number) => (
                                    <div className="w-1/3 p-1.5 flex items-center" key={`equipment_type_extra_${index}`}>
                                        <CheckBox
                                            id={`secondary_${vehicleType.value}`}
                                            name={vehicleType.label}
                                            checked={primaryEquipmentType === vehicleType.value || secondaryEquipmentTypes?.includes(vehicleType.value)}
                                            parentClassName="!w-3.5 !h-3.5"
                                            value={vehicleType.value}
                                            onChangeFunc={handleSecondaryEquipmentChange(vehicleType)}
                                            disabled={false}
                                        />
                                        <label htmlFor={`secondary_${vehicleType.value}`} className="text-textSecondary text-xs font-medium pl-1.5 cursor-pointer">
                                            {vehicleType.label}
                                        </label>
                                    </div>
                                ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Section 5: Additional Services ──────────────────────────────── */}
            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common flex items-center gap-x-0.5">
                        {t('QuotingHub.Common.Pickup_Additional_Services')}
                        <div className="cursor-pointer" onClick={() => setAction({ ...action, pickupService: true })}>
                            <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                        </div>
                    </label>
                </div>
                <div className="flex-1">
                    <div className="rounded-lg border border-utilityGray200 bg-white flex flex-wrap p-3 gap-3">{getList(serviceTypes?.PICKUP || [])}</div>
                </div>
            </div>

            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common flex items-center gap-x-0.5">
                        {t('QuotingHub.Common.Delivery_Additional_Services')}
                        <div className="cursor-pointer" onClick={() => setAction({ ...action, deliveryService: true })}>
                            <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                        </div>
                    </label>
                </div>
                <div className="flex-1 space-y-5">
                    <div className="rounded-lg border border-utilityGray200 bg-white flex flex-wrap p-3 gap-3">{getList(serviceTypes?.DELIVERY || [])}</div>
                </div>
            </div>

            <div className="border-t border-utilityGray200 stepper-content-container">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common flex items-center gap-x-0.5">
                        {t('Common.Other_Additional_Services')}
                        <div className="cursor-pointer" onClick={() => setAction({ ...action, otherService: true })}>
                            <InfoCircle className="h-3.5 w-3.5 cursor-pointer text-primary" />
                        </div>
                    </label>
                </div>
                <div className="flex-1 space-y-5">
                    <div className="rounded-lg border border-utilityGray200 bg-white flex flex-wrap p-3 gap-3">{getList(serviceTypes?.OTHER || [], ['load_insurance'])}</div>
                </div>
            </div>

            {/* ── Section 6: Load Insurance ────────────────────────────────────── */}
            <div className="border-t border-utilityGray200 stepper-content-container w-full">
                <div className="stepper-lable-wrap flex items-center justify-between">
                    <Controller
                        name="isLoadInsuranceEnabled"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <Toggle
                                isChecked={value || false}
                                onChange={handleLoadInsuranceToggle(onChange)}
                                label={t('Common.Load_Insurance')}
                                switchClassName="cursor-pointer"
                                className=""
                                name="isLoadInsuranceEnabled"
                                labelPosition="left"
                                id="isLoadInsuranceEnabled"
                                isDisabled={false}
                            />
                        )}
                    />
                </div>
                <div className="stepper-lable-content-wrap flex-1">
                    <div className="flex-wrap flex gap-x-2">
                        <Controller
                            name="insuranceCommercialValue"
                            control={control}
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                                <InputText
                                    parentClassName="flex-1"
                                    inputType="number"
                                    value={value}
                                    onChangeFunc={handleInsuranceCommercialValueChange(onChange)}
                                    placeholder="Enter commercial value"
                                    isError={!!error}
                                    disabled={!isLoadInsuranceEnabled}
                                    className="pl-8"
                                    shouldFormatNumber={true}
                                    icon={<CurrencyDollar className="input-currency-sign" />}
                                />
                            )}
                        />
                        <Controller
                            name="insuranceCurrencyType"
                            control={control}
                            render={({ field: { value, onChange } }) => (
                                <SelectBox
                                    options={currencyOptions}
                                    value={currencyOptions.find((option) => option.value === value)}
                                    onChangeFunc={handleInsuranceCurrencyChange(onChange)}
                                    parentClassName="min-w-[80px]"
                                    isDisabled={!isLoadInsuranceEnabled}
                                />
                            )}
                        />
                    </div>
                </div>
            </div>

            {/* ── Section 7: Special Notes ─────────────────────────────────────── */}
            <div className="stepper-content-container border-t pb-5 border-utilityGray200 border-b">
                <div className="stepper-lable-wrap flex">
                    <label className="stepper-label-common">Special Instruction</label>
                </div>
                <div className="flex-1">
                    <Controller
                        name="specialNotes"
                        control={control}
                        render={({ field: { value, onChange } }) => (
                            <InputTextArea inputName="specialNotes" placeholder="Enter any special instructions here" value={value} onChangeFunc={onChange} maxLength={200} rows={4} />
                        )}
                    />
                </div>
            </div>

            {/* ── Modals ───────────────────────────────────────────────────────── */}
            {(action.pickupService || action.deliveryService || action.otherService) && (
                <AdditionalServiceModal
                    handleClose={handleClose}
                    services={(action.otherService ? (serviceTypes?.OTHER || []).filter((s: any) => s.slug !== 'load_insurance') : serviceTypes?.[action.pickupService ? 'PICKUP' : 'DELIVERY']) || []}
                    title={`${action.pickupService ? 'Pickup' : action.deliveryService ? 'Delivery' : 'Other'} ${t('Common.Additional_Services')}`}
                />
            )}
            {action.inBond && <InBondModal handleClose={handleClose} handleInBond={handleInBond} defaultFormValues={watch('additionalServices') as unknown as IInBondForm} />}
            {action.loadInsurance && (
                <LoadInsuranceModal handleClose={handleClose} handleLoadInsurance={handleLoadInsurance} defaultFormValues={watch('additionalServices') as unknown as ILoadInsuranceForm} />
            )}
            {action.dangerousGoods && (
                <DangerousGoodsModal handleClose={handleClose} handleDangerousGoods={handleDangerousGoods} defaultFormValues={watch('additionalServices') as unknown as IDangerousGoodsForm} />
            )}
            {action.limitedAccess && (
                <LimitedAccessDeliveryModal
                    handleClose={handleClose}
                    handleLimitedAccess={handleLimitedAccess}
                    defaultFormValues={watch('additionalServices') as ILimitedAccessDeliveryDataForm | null}
                />
            )}
        </>
    );
};

export default FtlLoadShipmentDetails;
